import React from "react";
import Image from "next/image";
import { GetStaticProps, InferGetStaticPropsType } from "next";
import Link from "next/link";
import Testimonials from "@/components/testimonial/testimonial";
import { Button } from "@/components/ui/button";
import { HomeCatalogSection, ProductCollectionData } from "@/modules/landing/components/section-catalog";
import FloatingButton from '../components/floating-button/floating-button'
import { Header } from "../modules/consignment";
import { Footer } from "../modules/landing/components/footer";
import { SEO } from "../components/seo/seo";

type Data = {
  collections: ProductCollectionData[];
}

async function getRecommendationProducts(): Promise<ProductCollectionData[]> {
  try {
    const postsRes = await fetch(
      `${process.env.NEXT_PUBLIC_API_URL}/guest/featured-collections`
    );
    return postsRes?.json() ?? [];
  } catch (e) {
    return [];
  }
}

export const getStaticProps = (async () => {
  const collections = await getRecommendationProducts();
  function shortenTotalSoldNumber(value: number): string {
    if (value > 100 && value < 1000) {
      return (value - (value % 100)) + '+'
    } else if (value > 1000) {
      return ((value - (value % 1000)) / 1000) + 'k+'
    } else {
      return value.toString();
    }
  }
  return {
    props: {
      collections: (collections ?? []).map((c => ({
        ...c,
        products: (c.products ?? []).map(p => {
          const mainPrice = p.ProductUnits.length > 0 ? p.ProductUnits[0].price : 0;
          const nonPromoPrice = p.ProductUnits.length > 0 ? p.ProductUnits[0].discount_price ?? p.ProductUnits[0].base_price : 0;
          const save = nonPromoPrice - mainPrice;
          const savePercentage = Math.ceil((save / nonPromoPrice) * 100)
          return {
            ...p,
            main_price: mainPrice,
            main_picture_url: p.main_picture_url ?? (p.ProductImages.length > 0 ? p.ProductImages[0].image_url : null),
            nonpromo_price: nonPromoPrice,
            rating_number: 4 + ((Number(p.id[p.id.length - 1]) % 5 + 5) / 10),
            total_sold_number: shortenTotalSoldNumber(25000 * ((Number(p.id[p.id.length - 1]) % 6 + 5) / 10)),
            save,
            save_percentage: savePercentage,
          }
        })
      }))),
    }
  }
}) satisfies GetStaticProps<Data>

export default function Index({ collections }: InferGetStaticPropsType<typeof getStaticProps>) {

  return (
    <main className="flex flex-col justify-between bg-white">
      <SEO title="Jooalan" path="/" description="Mulai cuanmu sekarang" />
      <Header />
      <div className="flex flex-col items-center mt-24">
        <section className="max-w-screen-xl w-full relative xl:rounded-2xl overflow-hidden mb-6" style={{ height: 324 }}>
          <div className="absolute inset-0 w-max h-full">
            <Image src="/img/jooalan/home-banner.png" height={324} width={1280} alt="banner jooalan" />
          </div>
          <div className="absolute inset-0 h-full items-center sm:items-start text-center sm:text-left flex flex-col justify-center pl-4 sm:pl-12">
            <h1 className="text-white font-semibold text-xl sm:text-3xl">Supplier Andalan Para Juragan</h1>
            <h1 className="text-white font-bold mt-1 text-2xl sm:text-[3rem]">HARGA TIADA LAWAN!</h1>
            <Link href="/join" className="mt-6">
              <div className="bg-[#F9B920] max-w-max text-md font-bold px-4 py-3 rounded-3xl hover:bg-accent cursor-pointer animate-bounce">
                CEK BARANG & HARGA SEKARANG
              </div>
            </Link>
          </div>
        </section>
        <HomeCatalogSection collections={collections} />
        <section className="max-w-screen-xl w-full relative py-12">
          <div className="flex flex-col items-center w-full">
            <h1 className="text-black font-bold text-xl sm:text-3xl text-center">
              Mengapa Belanja di Jooalan?
            </h1>
          </div>
          <div className="flex flex-row justify-center">
            <div className="flex flex-row gap-8 sm:gap-12 mt-8 px-6 max-w-max w-full overflow-x-scroll" style={{
              scrollbarWidth: 'none'
            }}>
              <div className="flex flex-col items-center">
                <Image src="/img/jooalan/Step-1.png" alt="step-1-jooalan" width={300} height={300} />
                <div className="font-bold text-xl">Harga Terbaik</div>
                <div className="text-center w-[280px] mt-2 text-base">Kami hadirkan harga terbaik untuk juragan yang ingin mendapatkan untung maksimal.</div>
              </div>
              <div className="flex flex-col items-center">
                <Image src="/img/jooalan/Step-2.png" alt="step-1-jooalan" width={300} height={300} />
                <div className="font-bold text-xl">Proses Mudah</div>
                <div className="text-center w-[280px] mt-2 text-base">Pilih langsung barang-barang yang juragan ingin beli lewat katalog online kami.</div>
              </div>
              <div className="flex flex-col items-center">
                <Image src="/img/jooalan/Step-3.png" alt="step-1-jooalan" width={300} height={300} />
                <div className="font-bold text-xl">Terpercaya</div>
                <div className="text-center w-[320px] mt-2 text-base">Sejak 2021, kami telah dipercaya menyediakan barang-barang harga terbaik untuk juragan-juragan langganan kami.</div>
              </div>
            </div>
          </div>
          <div className="flex flex-row justify-center mt-12 w-full">
            <Link href="/join">
              <Button className="text-white rounded-3xl max-w-max font-bold text-sm sm:text-md px-3 py-2 sm:px-4 sm:py-6 animate-bounce">
                CEK BARANG & HARGA
              </Button>
            </Link>
          </div>
        </section>
        <div className="max-w-screen-xl w-full border-t border-t-gray-300" />
        <section className="max-w-screen-xl w-screen relative py-12">
          <Testimonials />
        </section>
        <section className="relative from-[#4c0202] to-primary w-screen flex flex-row justify-center" style={{ backgroundImage: 'linear-gradient(to right, var(--tw-gradient-stops))' }}>
          <div className="hidden sm:flex flex-row justify-between max-w-screen-xl w-screen py-12 px-10">
            <div>
              <h1 className="text-white font-bold text-xl sm:text-3xl ">Siap Tingkatkan Keuntungan Bisnis Anda?</h1>
              <div className="text-white text-md mt-2 pr-24">
                Bergabunglah sekarang dan dapatkan akses ke produk termurah yang langsung bisa Anda jual kembali dengan margin tinggi
              </div>
            </div>
            <Link href="/join">
              <div className="mt-6 bg-[#F9B920] max-w-max text-md font-bold px-4 py-3 rounded-3xl hover:bg-accent cursor-pointer whitespace-nowrap">
                CEK BARANG & HARGA
              </div>
            </Link>
          </div>
          <div className="flex sm:hidden flex-col text-center items-center py-8 px-2">
            <div className="text-white font-bold text-xl">Siap Tingkatkan Keuntungan Bisnis Anda?</div>
            <div className="text-white text-sm mt-2">
              Bergabunglah sekarang dan dapatkan akses ke produk termurah yang langsung bisa Anda jual kembali dengan margin tinggi
            </div>
            <Link href="/join" className="mt-6 bg-[#F9B920] max-w-max text-sm font-bold px-3 py-2 rounded-3xl hover:bg-accent cursor-pointer whitespace-nowrap">
              CEK BARANG & HARGA
            </Link>
          </div>
        </section>
      </div>
      <FloatingButton />
      <Footer />
    </main>
  );
}
